import {
  ISectionType,
  ICourse,
  ISection,
  ITimeSliceTile,
  ISectionTime,
  ICalendarSection
} from '../../../interfaces/scheduler_interfaces';
import * as _4 from 'lodash4';
import { ITermSessionsDatasource } from '../datasources/term_sessions_datasource';
import { TermProgramInfoModel } from '../models/term_program_info_model';
export interface ISessionDates {
  timeSliceStartDate: string;
  timeSliceEndDate: string;
  sessionStartTimeStamp: number;
}
export class SectionTypeFilters {
  constructor(
    private ngTermSessionsDatasource: ITermSessionsDatasource,
    private ngTermProgramInfoModel: TermProgramInfoModel
  ) {}
  activeSectionTypes(sectionTypes: ISectionType[]): ISectionType[] {
    return _4.filter(sectionTypes, (sectionType: ISectionType) => sectionType.is_active);
  }
  validSectionTypes(sectionTypes: ISectionType[]): ISectionType[] {
    return this.activeSectionTypes(sectionTypes);
  }
  getSectionTypesForCourse(sectionTypes: ISectionType[], course: ICourse): ISectionType[] {
    return _4.sortBy(
      _4.filter(sectionTypes, (sectionType) => sectionType.course === course.id),
      [(sectionType: ISectionType) => sectionType.rank]
    );
  }
  getSessionSectionTypesForCourse(sectionTypesForCourse: ISectionType[]) {
    const hasSessions = !_4.isEmpty(
      _4.compact(_4.map(sectionTypesForCourse, (sectionType: ISectionType) => sectionType.session))
    );
    if (!hasSessions) {
      return [
        {
          session: '-1',
          timeSliceStartDate: '',
          timeSliceEndDate: '',
          sessionStartTimeStamp: 0,
          section_types: sectionTypesForCourse
        }
      ];
    }

    const courseSessions = _4.compact(
      _4.map(_4.groupBy(sectionTypesForCourse, 'session'), (value, sessionId: string) => {
        const { timeSliceStartDate, timeSliceEndDate, sessionStartTimeStamp } = this.getSessionDates(sessionId);
        return {
          session: sessionId,
          sessionStartTimeStamp,
          timeSliceStartDate,
          timeSliceEndDate,
          section_types: value
        };
      })
    );
    return _4.sortBy(courseSessions, 'sessionStartTimeStamp');
  }
  getSessionDates(sessionId: string): ISessionDates {
    const DATE_WITH_YEAR_FORMAT = 'MMM Do YYYY';
    const sessionsData = this.ngTermSessionsDatasource.read();
    const startDate = _4.get(sessionsData, `${sessionId}.session_start_dt`, '');
    const endDate = _4.get(sessionsData, `${sessionId}.session_end_dt`, '');
    if (!_4.isEmpty(startDate) && !_4.isEmpty(endDate)) {
      const sessionStart = moment(startDate, 'YYYY-MM-DD');
      const sessionStartTimeStamp = sessionStart.valueOf();
      const sessionEnd = moment(endDate, 'YYYY-MM-DD');
      const format = sessionStart.year() !== sessionEnd.year() ? DATE_WITH_YEAR_FORMAT : 'MMM Do';
      const timeSliceStartDate = sessionStart.format(format);
      const timeSliceEndDate = sessionEnd.format(DATE_WITH_YEAR_FORMAT);
      return {
        timeSliceStartDate,
        timeSliceEndDate,
        sessionStartTimeStamp
      };
    }
    return {
      timeSliceStartDate: '',
      timeSliceEndDate: '',
      sessionStartTimeStamp: 0
    };
  }
  isSectionDurationShorterThanTerm(section: ISection) {
    const ACCELERATED_THRESHOLD = 75;
    const termData = this.ngTermProgramInfoModel.read().term;
    if (!termData) {
      return false;
    }
    const termStart = moment(termData.term_start_dt);
    const termEnd = moment(termData.term_end_dt);
    const sectionStart = moment(section.class_start_dt);
    const sectionEnd = moment(section.class_end_dt);

    const sectionDays = sectionEnd.diff(sectionStart, 'days');
    const termDays = termEnd.diff(termStart, 'days');
    return (sectionDays / termDays) * 100 < ACCELERATED_THRESHOLD;
  }

  createVirtualWeek(start: moment.Moment, end: moment.Moment) {
    const week: Record<string, moment.Moment> = {};
    const weekStart: moment.Moment = moment(start);
    for (let i = 1; i < 8; i++) {
      week[i] = moment(weekStart);
      weekStart.add(1, 'day');
    }
    return week;
  }

  timeSliceFilter(sections: ICalendarSection[], selectedSessionDates: ITimeSliceTile) {
    if (_4.isEmpty(selectedSessionDates.timeSliceStartDate)) {
      return sections;
    }
    const sliceStart = moment(selectedSessionDates.timeSliceStartDate);
    const sliceEnd = moment(selectedSessionDates.timeSliceEndDate);

    const sectionsToReturn: ICalendarSection[] = [];
    const virtualWeek = this.createVirtualWeek(sliceStart, sliceEnd);

    _4.forEach(sections, (calendarTime: ICalendarSection) => {
      const fromDate: string = _4.get(calendarTime, 'from_dt') || _4.get(calendarTime, 'section.class_start_dt');
      const toDate: string = _4.get(calendarTime, 'to_dt') || _4.get(calendarTime, 'section.class_end_dt');
      const isBusyTimeOrNoSectionTime = _4.isEmpty(fromDate) && _4.isEmpty(toDate);
      const sectionOnline = calendarTime.day_of_week === 8;

      if (isBusyTimeOrNoSectionTime) {
        sectionsToReturn.push(calendarTime);
      }

      if (
        !sectionOnline &&
        virtualWeek[calendarTime.day_of_week].isSameOrBefore(moment(toDate)) &&
        this.isSectionInTimeRange(sliceStart, sliceEnd, calendarTime)
      ) {
        sectionsToReturn.push(calendarTime);
      }

      if (sectionOnline && this.isSectionInTimeRange(sliceStart, sliceEnd, calendarTime)) {
        sectionsToReturn.push(calendarTime);
      }
    });
    return sectionsToReturn;
  }

  isSectionInTimeRange(sliceStart: moment.Moment, sliceEnd: moment.Moment, sectionTime: ICalendarSection) {
    let sectionStart;
    let sectionEnd;
    let sectionIsNotWithinSlice;
    let sectionIsBetweenSlice;
    let sectionIntersectsWithSliceStart;
    let sectionIntersectsWithSliceEnd;
    let sectionIsLargerThanSlice;

    const sectionFromDt = _4.get(sectionTime, 'from_dt', '') || _4.get(sectionTime, 'section.class_start_dt', '');
    const sectionToDt = _4.get(sectionTime, 'to_dt', '') || _4.get(sectionTime, 'section.class_end_dt', '');

    sectionStart = moment(sectionFromDt).startOf('day');
    sectionEnd = moment(sectionToDt).endOf('day');
    sectionIsNotWithinSlice = sliceStart.isSameOrAfter(sectionEnd) || sliceEnd.isSameOrBefore(sectionStart);
    sectionIsBetweenSlice = sliceStart.isSameOrBefore(sectionStart) && sliceEnd.isSameOrAfter(sectionEnd);
    sectionIntersectsWithSliceStart = sliceStart.isSameOrAfter(sectionStart) && sliceEnd.isSameOrAfter(sectionEnd);
    sectionIntersectsWithSliceEnd = sliceStart.isSameOrBefore(sectionStart) && sliceEnd.isSameOrBefore(sectionEnd);
    sectionIsLargerThanSlice = sliceStart.isSameOrAfter(sectionStart) && sliceEnd.isSameOrBefore(sectionEnd);

    return (
      !sectionIsNotWithinSlice &&
      (sectionIsBetweenSlice ||
        sectionIntersectsWithSliceStart ||
        sectionIntersectsWithSliceEnd ||
        sectionIsLargerThanSlice)
    );
  }
}

angular.module('capability-ng-scheduler').service('ngSectionTypeFilters', SectionTypeFilters);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/term_courses/filters/section_type_filters.ts